import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = sectionHTML ? new DOMParser().parseFromString(sectionHTML, 'text/html') : null;

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (sectionHTML && newSectionHTML) {
          morphSection(this.sectionId, sectionHTML).catch((error) => console.error(error));
          this.#syncCartItemsComponent(newSectionHTML);
        }

        this.#syncCartPricesFromCart(parsedResponseText);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const newSectionHTML = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
      morphSection(this.sectionId, cartItemsHtml).catch((error) => console.error(error));
      this.#syncCartItemsComponent(newSectionHTML);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Formats Shopify cart API cent values in the active storefront currency.
   * @param {number} cents
   * @param {string} [currency]
   * @returns {string}
   */
  #formatCartMoney(cents, currency) {
    const activeCurrency = currency || window.Shopify?.currency?.active || 'GBP';
    const locale = document.documentElement.lang || navigator.language || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency,
      })
        .format(cents / 100)
        .replace(/([.,]00)(?!\d)/, '');
    } catch (_error) {
      return `${activeCurrency} ${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
    }
  }

  /**
   * Updates a text-component without depending on full section morphing.
   * @param {Element | null} element
   * @param {string} value
   */
  #setTextComponentValue(element, value) {
    if (!element) return;

    element.textContent = value;
    element.setAttribute('value', value);
    element.removeAttribute('shimmer');
  }

  /**
   * Keeps visible line prices and totals correct even if Shopify section morphing is stale.
   * @param {Object} updatedCart
   * @param {Array<{key: string, quantity: number, variant_id: number, final_line_price: number, handle?: string}>} [updatedCart.items]
   * @param {number} [updatedCart.total_price]
   * @param {string} [updatedCart.currency]
   */
  #syncCartPricesFromCart(updatedCart) {
    if (!updatedCart.items) return;

    const currency = updatedCart.currency;
    const cartComponents = document.querySelectorAll('cart-items-component');

    for (const component of cartComponents) {
      if (!(component instanceof HTMLElement)) continue;

      for (const [index, item] of updatedCart.items.entries()) {
        const escapedKey = window.CSS?.escape
          ? CSS.escape(item.key)
          : item.key.replace(/["\\]/g, '\\$&');
        const row =
          component.querySelector(`.cart-items__table-row[data-key="${escapedKey}"]`) ||
          component.querySelectorAll('.cart-items__table-row[data-key]')[index];

        if (!(row instanceof HTMLElement)) continue;

        const quantityInput = row.querySelector('cart-quantity-selector-component input, quantity-selector-component input');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = item.quantity.toString();
          quantityInput.defaultValue = item.quantity.toString();
          quantityInput.setAttribute('data-cart-quantity', item.quantity.toString());
        }

        this.#setTextComponentValue(
          row.querySelector('.cart-items__price text-component'),
          this.#formatCartMoney(item.final_line_price, currency)
        );
      }

      if (typeof updatedCart.total_price === 'number') {
        const ukSocksPromoDiscount = this.#getUkSocksPromoDiscount(updatedCart);
        const displayTotal = Math.max(updatedCart.total_price - ukSocksPromoDiscount, 0);

        this.#setTextComponentValue(
          component.querySelector('[data-cart-subtotal], .cart__total-value'),
          this.#formatCartMoney(displayTotal, currency)
        );
      }
    }
  }

  /**
   * Mirrors the UK-only "2 socks for £20" estimated drawer discount.
   * Shopify applies the code at checkout; the drawer shows it before checkout.
   * @param {Object} updatedCart
   * @param {Array<{quantity: number, handle?: string}>} [updatedCart.items]
   * @param {string} [updatedCart.currency]
   * @returns {number}
   */
  #getUkSocksPromoDiscount(updatedCart) {
    const currency = updatedCart.currency || window.Shopify?.currency?.active;
    const country = window.Shopify?.country || document.cookie.match(/(?:^|; )localization=([^;]+)/)?.[1];

    if (country !== 'GB' || currency !== 'GBP' || !updatedCart.items) return 0;

    const sockQuantity = updatedCart.items.reduce((total, item) => {
      if (item.handle === 'no-socks' || item.handle === 'no-socks-white') {
        return total + item.quantity;
      }

      return total;
    }, 0);

    return Math.floor(sockQuantity / 2) * 400;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Keeps the drawer totals and line prices in sync on browsers where full-section morphing can leave cart UI stale.
   * @param {Document} newSectionHTML
   */
  #syncCartItemsComponent(newSectionHTML) {
    const freshCartItemsComponent = Array.from(newSectionHTML.querySelectorAll('cart-items-component')).find(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === this.sectionId
    );
    const currentCartItemsComponent = Array.from(document.querySelectorAll('cart-items-component')).find(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === this.sectionId
    );

    if (!freshCartItemsComponent || !currentCartItemsComponent) return;

    currentCartItemsComponent.innerHTML = freshCartItemsComponent.innerHTML;
    /** @type {any} */ (currentCartItemsComponent).updatedCallback?.();
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
